import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function ArticleSunshineCoastVancouver() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "sunshine-coast-vancouver-trip";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "getting-there": "Getting There",
    "attractions": "Top Attractions & Activities",
    "best-time": "Best Time to Visit & Seasonal Tips",
    "accommodation": "Where to Stay",
    "local-regulations": "Local Regulations & Best Practices",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://www.sunshinecoastcanada.com",
      label: "Sunshine Coast Official Tourism"
    },
    {
      url: "https://www.bcferries.com/routes-fares/schedules/daily/HSB-LNG",
      label: "BC Ferries Schedule & Reservations"
    },
    {
      url: "https://sunshinecoasttrail.com",
      label: "Sunshine Coast Trail Official Site"
    }
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // Provide as many locations as needed. Each location's "id" must match the
  // ref you use in the corresponding <p ref={(el) => setSpotRef(id, el)}> or similar.
  const locations = [
    {
      id: "loc-gibsons-landing",
      lat: 49.40111,
      lng: -123.50750,
      popupText: "Gibsons Landing"
    },
    {
      id: "loc-soames-hill-trailhead",
      lat: 49.42112,
      lng: -123.48853,
      popupText: "Soames Hill Trailhead"
    },
    {
      id: "loc-smuggler-cove",
      lat: 49.51611,
      lng: -123.95750,
      popupText: "Smuggler Cove Marine Provincial Park"
    },
    {
      id: "loc-skookumchuck",
      lat: 49.75008,
      lng: -123.93638,
      popupText: "Skookumchuck Narrows"
    },
    {
      id: "loc-tin-hat-mountain",
      lat: 49.98261,
      lng: -124.38143,
      popupText: "Tin Hat Mountain (Sunshine Coast Trail)"
    },
    {
      id: "loc-desolation-sound",
      lat: 50.10250,
      lng: -124.70000,
      popupText: "Desolation Sound Marine Park"
    }
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>{ref_data?.title || "Sunshine Coast Trip from Vancouver"}</title>
        <meta
          name="description"
          content={
            ref_data?.excerpt ||
            "A comprehensive guide to traveling from Vancouver to BC's Sunshine Coast."
          }
        />
        <meta
          name="keywords"
          content="Sunshine Coast, Vancouver, Travel Guide, BC Ferries, Hiking, Attractions"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href={`https://www.bcouver.com/articles/${ref_id}.html`}
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content={ref_data?.title || "Sunshine Coast Trip from Vancouver"}
        />
        <meta
          property="og:description"
          content={
            ref_data?.excerpt ||
            "A comprehensive guide to traveling from Vancouver to BC's Sunshine Coast."
          }
        />
        <meta
          property="og:image"
          content={`https://bcouver.com/assets/images/${ref_id}.jpg`}
        />
        <meta
          property="og:url"
          content={`https://www.bcouver.com/articles/${ref_id}.html`}
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={ref_data?.title || "Sunshine Coast Trip from Vancouver"}
        />
        <meta
          name="twitter:description"
          content={
            ref_data?.excerpt ||
            "A comprehensive guide to traveling from Vancouver to BC's Sunshine Coast."
          }
        />
        <meta
          name="twitter:image"
          content={`https://bcouver.com/assets/images/${ref_id}.jpg`}
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={ref_data?.image || "https://bcouver.com/assets/images/article-fallback.jpg"}
              alt={ref_data?.["image-desc"] || "Sunshine Coast Trip from Vancouver"}
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia/>

            {/* SECTION: Introduction */}
            <h2 id="intro" ref={(el) => setSpotRef("intro", el)} style={{ scrollMarginTop: "80px" }}>
              Introduction
            </h2>
            <p>
              The Sunshine Coast of British Columbia is a hidden gem that sits on
              the mainland northwest of Vancouver—yet is only accessible by ferry
              or floatplane. With beautiful forests, charming coastal towns, and
              year-round outdoor activities, it’s the perfect getaway for families,
              solo adventurers, and everyone in between. In this guide, we’ll walk
              you through how to get there, must-see attractions, the best times
              to visit, accommodations, local regulations, and answers to the most
              frequently asked questions.
            </p>

            {/* SECTION: Getting There */}
            <h2
              id="getting-there"
              ref={(el) => setSpotRef("getting-there", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              Getting There
            </h2>
            <p>
              To reach the Sunshine Coast from Vancouver, you’ll typically drive or
              take public transit to Horseshoe Bay in West Vancouver, then board the
              BC Ferries route to Langdale. The scenic 40-minute ferry ride offers
              beautiful views of Howe Sound. Once you arrive at Langdale, Highway 101
              (the Sunshine Coast Highway) connects the coastal communities.
            </p>
            <p>
              Alternatively, seaplane flights from downtown Vancouver or YVR to
              Sechelt or Powell River are quick and thrilling, providing a bird’s-eye
              view of the coastline. Budget around CA$14 per adult and about CA$46 per
              vehicle (driver included) for a one-way ferry crossing, though return is
              free for foot passengers on certain routes. Book ahead in peak seasons to
              avoid long waits.
            </p>

            {/* SECTION: Top Attractions & Activities */}
            <h2
              id="attractions"
              ref={(el) => setSpotRef("attractions", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              Top Attractions & Activities
            </h2>
            <p>
              The Sunshine Coast features a blend of picturesque towns, marine parks,
              and forested trails. Below are five must-see spots:
            </p>

            {/* Gibsons Landing */}
            <p
              ref={(el) => setSpotRef("loc-gibsons-landing", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Gibsons Landing</strong>{" "}
              <Marker
                coordY={49.40111}
                coordX={-123.50750}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – This quaint harbour town is your first stop off the ferry. Stroll
              the waterfront for shops, galleries, and iconic places like Molly’s
              Reach Café. If you’re up for a short hike, head to{" "}
              <strong>Soames Hill</strong> for panoramic views.
            </p>

            {/* Soames Hill Trailhead */}
            <p
              ref={(el) => setSpotRef("loc-soames-hill-trailhead", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Soames Hill Trailhead</strong>{" "}
              <Marker
                coordY={49.42112}
                coordX={-123.48853}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – The starting point of a brief but steep hike that rewards
              adventurers with sweeping vistas of Howe Sound, Gibsons, and
              surrounding mountains.
            </p>

            {/* Smuggler Cove */}
            <p
              ref={(el) => setSpotRef("loc-smuggler-cove", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Smuggler Cove Marine Provincial Park</strong>{" "}
              <Marker
                coordY={49.51611}
                coordX={-123.95750}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – Located near Halfmoon Bay, a gentle 4 km round-trip trail
              leads to this gorgeous cove. Along the way, traverse wetlands on
              boardwalks and discover calm waters perfect for a picnic and wildlife
              spotting.
            </p>

            {/* Skookumchuck Narrows */}
            <p
              ref={(el) => setSpotRef("loc-skookumchuck", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Skookumchuck Narrows</strong>{" "}
              <Marker
                coordY={49.75008}
                coordX={-123.93638}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – Famous for its fierce tidal rapids, where billions of gallons of
              water surge through a narrow channel. A 4 km forest hike from Egmont
              gets you to prime viewing points. Check tide tables for the best times
              to see wild whirlpools and standing waves.
            </p>

            {/* Tin Hat Mountain */}
            <p
              ref={(el) => setSpotRef("loc-tin-hat-mountain", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Tin Hat Mountain</strong>{" "}
              <Marker
                coordY={49.98261}
                coordX={-124.38143}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – Part of the 180 km Sunshine Coast Trail, this summit offers
              panoramic 360° views of lakes and peaks. A rustic hut at the top makes
              it a favorite for overnight hikers.
            </p>

            {/* Desolation Sound */}
            <p
              ref={(el) => setSpotRef("loc-desolation-sound", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Desolation Sound Marine Park</strong>{" "}
              <Marker
                coordY={50.10250}
                coordX={-124.70000}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – North of Powell River and accessible via Lund, this vast marine
              park is a paddler’s paradise known for warm swimming waters, abundant
              marine life, and remote wilderness camp spots. Guided kayak tours are
              available for those who want a taste of the region’s pristine beauty.
            </p>

            {/* SECTION: Best Time to Visit & Seasonal Tips */}
            <h2
              id="best-time"
              ref={(el) => setSpotRef("best-time", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              Best Time to Visit & Seasonal Tips
            </h2>
            <p>
              <strong>Summer (June–August):</strong> Expect warm, sunny days
              perfect for beach outings, kayaking, and hiking. Ferry lines can be
              long, so reservations are advised. This is peak season for festivals
              and markets.
            </p>
            <p>
              <strong>Winter (November–February):</strong> Mild, wet conditions
              prevail at sea level, with possible snow at higher elevations. Storm
              watching, cozy cabin stays, and quieter trails attract those who don’t
              mind some rain. Check ferry advisories in case of stormy weather.
            </p>
            <p>
              <strong>Spring & Fall:</strong> These shoulder seasons bring fewer
              crowds, moderate weather, and vibrant landscapes. Perfect for hiking,
              photography, and local events without the peak-season bustle.
            </p>

            {/* SECTION: Where to Stay */}
            <h2
              id="accommodation"
              ref={(el) => setSpotRef("accommodation", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              Where to Stay
            </h2>
            <p>
              Accommodation options range from affordable campsites and hostels to
              boutique B&amp;Bs, cozy cabins, and luxury resorts. Provincial parks
              like Porpoise Bay or private campgrounds near Pender Harbour offer
              nature immersion. For mid-range comfort, try local B&amp;Bs in Gibsons
              or Sechelt with home-cooked breakfasts and ocean vistas. Upscale
              travelers can opt for spa resorts like Rockwater Secret Cove or
              Painted Boat Resort in Pender Harbour. Book well in advance during
              summer to secure the best spots.
            </p>

            {/* SECTION: Local Regulations & Best Practices */}
            <h2
              id="local-regulations"
              ref={(el) => setSpotRef("local-regulations", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              Local Regulations & Best Practices
            </h2>
            <p>
              Ferry travelers must follow BC Ferries check-in rules: arrive early
              and consider reserving in high season. While driving along Highway 101,
              respect speed limits and watch for wildlife. Bears, cougars, and other
              fauna roam the forests, so store food securely and never feed or
              approach wild animals. Camping requires adherence to fire bans in
              summer—always check local fire danger ratings. Leave no trace on hikes
              by packing out all garbage, and stay on designated trails to protect
              fragile ecosystems. Marine wildlife is protected; maintain a safe
              distance from orcas, seals, and other creatures when boating or
              kayaking. As always, be mindful of First Nations territory; treat any
              cultural or historical sites with respect.
            </p>

            {/* SECTION: FAQ */}
            <h2
              id="faq"
              ref={(el) => setSpotRef("faq", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              FAQ
            </h2>
            <div className="faq-section">
              <h3>1. Do I need a car to enjoy the Sunshine Coast?</h3>
              <p>
                While having a car offers convenience for exploring remote areas,
                it’s not strictly necessary. Ferries accommodate foot passengers,
                and BC Transit buses run between key communities. Some visitors
                also opt for bike touring.
              </p>

              <h3>2. How many days should I plan for a trip?</h3>
              <p>
                A weekend getaway lets you sample highlights like Gibsons and
                Sechelt, but 3–5 days is ideal to include day trips to Skookumchuck,
                Pender Harbour, and Powell River. For backcountry hiking or time in
                Desolation Sound, consider a full week.
              </p>

              <h3>3. Are there family-friendly activities?</h3>
              <p>
                Absolutely. Many trails are easy enough for kids, and beaches with
                gentle waters are great for swimming. Towns like Sechelt have
                playgrounds near the shore, and local museums or markets offer fun
                cultural experiences.
              </p>
            </div>

            {/* SECTION: Conclusion */}
            <h2
              id="conclusion"
              ref={(el) => setSpotRef("conclusion", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              Conclusion
            </h2>
            <p>
              From its charming small towns to pristine marine parks and lush forests,
              the Sunshine Coast offers something for every traveler. With this guide
              in hand, you’re ready to explore hidden coves, hike stunning vistas, and
              immerse yourself in the relaxed coastal lifestyle. Whether you’re drawn
              to outdoor adventures or peaceful seaside retreats, this ferry-accessible
              paradise is well worth the journey.
            </p>
            <p>
              <em>
                Disclaimer: Always check official resources for ferry schedules,
                local advisories, and weather updates. Respect wildlife, follow
                safety guidelines, and leave no trace to preserve the region’s
                natural beauty.
              </em>
            </p>

            {/* BOTTOM COMPONENT: references, author, etc. */}
            <Bottom
              referencesData={referencesData}
              ref_data={ref_data}
            />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}